import type { UniversalIssueWithScreenshot } from '@deque/axe-devtools-issues-utils';
import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { Code, Tag, TagLabel } from '@deque/cauldron-react';

import NoScreenshot from './NoScreenshot';
import ScreenshotThumbnail from './ScreenshotThumbnail';
import { SupportedIntegrationProductSlugs } from '../../../common/constants';
import IntegrationIssueLink from '../../../app/components/integrations/IntegrationIssueLink';
import styles from './IssueDetails.css';
import { useFeatureFlagState } from '../../contexts/featureFlags';

interface Props {
  issue: UniversalIssueWithScreenshot;
  issueOrder: number;
  noScreenshotExplanation?: React.ReactNode;
}

const IssueDetails: React.FC<Props> = ({
  issue,
  issueOrder,
  noScreenshotExplanation
}) => {
  const { t } = useTranslation();
  const hasIntegrationsV1 = useFeatureFlagState('integrations_v1');

  //converts iso date in " Date at HH:MM " format
  const formattedCreatedAtString = useMemo(() => {
    const dateString = new Date(issue.createdAt).toLocaleDateString();
    const timeString = new Date(issue.createdAt)
      .toLocaleTimeString('default', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
      .toLocaleUpperCase();
    return t('{{dateString}} at {{timeString}}', {
      dateString,
      timeString
    });
  }, [t, issue.createdAt]);

  const impact =
    issue.impact &&
    {
      blocker: t('Blocker'),
      minor: t('Minor'),
      moderate: t('Moderate'),
      serious: t('Serious'),
      critical: t('Critical')
    }[issue.impact];

  return (
    <div className={styles.wrap}>
      {hasIntegrationsV1 && issue.integrationUrl && (
        <IntegrationIssueLink
          issue={issue}
          productSlug={SupportedIntegrationProductSlugs.jiraIntegration}
        />
      )}
      <div
        className={classNames({
          [styles.imageTextContainer]:
            !!issue.screenshotId || !!noScreenshotExplanation
        })}
      >
        {issue.screenshotId && (
          <ScreenshotThumbnail
            screenshotId={issue.screenshotId}
            issueOrder={issueOrder}
            issueHelp={issue.help}
            boundingBox={issue.boundingBox}
          />
        )}
        {!issue.screenshotId && noScreenshotExplanation && (
          <NoScreenshot explanation={noScreenshotExplanation} />
        )}
        <div>
          <h3>{t('Issue Description')}</h3>
          <p>{issue.description}</p>
        </div>
      </div>
      {issue.selector && (
        <>
          <h3>{t('Element Location')}</h3>
          <Code>{issue.selector.join(' ')}</Code>
        </>
      )}
      {issue.source && (
        <>
          <h3>{t('Element Source Code')}</h3>
          <Code language="html">{issue.source}</Code>
        </>
      )}
      <div className={styles.footer}>
        {!issue.needsReview && impact && (
          <Tag>
            <TagLabel>{t('Impact:')}</TagLabel>
            {impact}
          </Tag>
        )}
        <Tag>
          <TagLabel>{t('Found on:')}</TagLabel>
          {formattedCreatedAtString}
        </Tag>
        {issue.isManual && (
          <Tag>
            <Trans>
              <TagLabel>Found:</TagLabel>
              Manually
            </Trans>
          </Tag>
        )}
      </div>
    </div>
  );
};

export default IssueDetails;
